// DELTA, ported from internal/wire/delta.go.
//
// The pixels that changed in one chunk during one tick.
//
//	u16 chunkId
//	u16 recordCount
//	recordCount x (u16 offset | u16 countryId | u32 hp | u8 flags)
//
// The offset is chunk-relative, and that is what buys the u32 HP field: a global
// u32 pixel id would have spent the same nine bytes on a u16 HP, capping a 50x
// fortress at 65,535 clicks — a ceiling in a field width rather than in a tunable.
//
// A DELTA is only ever sent for a chunk the tick's TICK message marked dirty, and
// the client uses that to tell a quiet chunk from a lost frame.
//
// A delta never says who did it. Attribution for the SPEC §15 arcs rides
// EPHEMERAL instead.
//
// Decoded into parallel typed arrays rather than an array of record objects. A
// busy tick can carry a thousand records across the viewport, and the allocation
// of an object per record is the same mistake as an object per pixel, one order
// of magnitude down.

import { Reader, Writer } from './bytes';
import { ClassDelta } from './classes';
import { PixelsPerChunk, pixelOf, validChunk, validOffset, type ChunkID, type PixelID, type TickNo } from './core';
import { errChunkID, errOffset, errTooManyRecords, errTruncated } from './errors';

/** Nine bytes. */
export const DeltaRecordSize = 2 + 2 + 4 + 1;

/**
 * The ceiling the count field and the chunk itself impose: a chunk cannot change
 * more pixels than it holds, because the tick coalesces repeated hits on one
 * pixel into its final state.
 */
export const MaxDeltaRecords = PixelsPerChunk;

/** One chunk's changes for one tick, in parallel arrays of length `count`. */
export interface Delta {
  tick: TickNo;
  chunk: ChunkID;
  count: number;
  /** Chunk-relative offsets, the value that travels on the wire. */
  offset: Uint16Array;
  country: Uint16Array;
  hp: Uint32Array;
  flags: Uint8Array;
}

/** The global pixel id of record i. The inverse of the encoder's addressing half. */
export function deltaPixelID(d: Delta, i: number): PixelID {
  return pixelOf(d.chunk, d.offset[i]!);
}

export function decodeDelta(b: Uint8Array): Delta {
  const r = new Reader(b);
  const tick = r.header(ClassDelta);
  const chunk = r.u16();
  const n = r.u16();
  if (!validChunk(chunk)) throw errChunkID();
  if (n > MaxDeltaRecords) throw errTooManyRecords();
  if (n * DeltaRecordSize > r.remaining) throw errTruncated();

  const offset = new Uint16Array(n);
  const country = new Uint16Array(n);
  const hp = new Uint32Array(n);
  const flags = new Uint8Array(n);
  for (let i = 0; i < n; i++) {
    const off = r.u16();
    country[i] = r.u16();
    hp[i] = r.u32();
    flags[i] = r.u8();
    if (!validOffset(off)) throw errOffset();
    offset[i] = off;
  }
  r.done();
  return { tick, chunk, count: n, offset, country, hp, flags };
}

/** One record, for the encoder. The decoder never produces these. */
export interface DeltaRecord {
  offset: number;
  country: number;
  hp: number;
  flags: number;
}

/**
 * The encoder is server-side, and exists here only so a test can build a frame
 * the decoder is then run against without reaching for a fixture file.
 */
export function encodeDelta(d: { tick: TickNo; chunk: ChunkID; records: readonly DeltaRecord[] }): Uint8Array {
  if (!validChunk(d.chunk)) throw errChunkID();
  if (d.records.length > MaxDeltaRecords) throw errTooManyRecords();
  for (const rec of d.records) if (!validOffset(rec.offset)) throw errOffset();
  const w = new Writer(9 + 4 + d.records.length * DeltaRecordSize);
  w.header(ClassDelta, d.tick).u16(d.chunk).u16(d.records.length);
  for (const rec of d.records) {
    w.u16(rec.offset).u16(rec.country).u32(rec.hp).u8(rec.flags);
  }
  return w.finish();
}
